import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/services/api'
import router from '@/router';

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    products: [],
    initMessage: '',
    conversationId: null,
    conversationState: null,
    messages: [],
    userEmail:'',
    selectedProductId:null,
    selectedProductName: null,
    isInitialized: false, 
    isProductSelected: false,
    isLoading:false,
    error:null,
    isAuthenticated: false,
    authError: null
  },
  getters: {
    isInitialized: (state) => state.isInitialized,
    isProductSelected: (state) => state.isProductSelected,
    hasActiveConversation: (state) => state.conversationId !== null,
    allMessages: (state) => state.messages,
    selectedProduct: (state) => {
      if (!state.selectedProductId) return null;
      return {
        id: state.selectedProductId,
        name: state.selectedProductName
      };
    },
    isLoading: (state) => state.isLoading,
    errorMessage: (state) => state.error,
    productsList: (state) => state.products,
    isAuthenticated: (state) => state.isAuthenticated,
    authError: (state) => state.authError,
    userEmail: (state) => state.userEmail
  },
  mutations: {
    SET_PRODUCTS(state,products){
      state.products=products;
    },
    SET_USER_EMAIL(state,email) {
      state.userEmail = email;
    },
    SET_SELECTED_PRODUCT(state, { productId, productName }) {
      state.selectedProductId = productId;
      state.selectedProductName = productName;
    },
    SET_CONVERSATION_ID(state,conversationId){
      state.conversationId = conversationId
    },
    SET_CONVERSATION_STATE(state, conversationState) {
      state.conversationState = conversationState;
    },
    ADD_MESSAGE(state, message) {
      state.messages.push(message);
    },
    SET_LOADING(state,isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state,error) {
      state.error = error;
    },
    SET_INIT_MESSAGE(state, message) {
      state.initMessage = message;
    },
    SET_INITIALIZED(state, value) {
      state.isInitialized = value;
    },
    SET_PRODUCT_SELECTED(state, value) {
      state.isProductSelected = value;
    },
    SET_AUTHENTICATED(state, value) {
      state.isAuthenticated = value;
    },
    SET_AUTH_ERROR(state, error) {
      state.authError = error;
    },
    RESET_CONVERSATION(state) {
      state.conversationId = null;
      state.conversationState = null;
      state.messages = [];
      state.selectedProductId = null;
      state.selectedProductName = null;
      state.isProductSelected = false;
      state.isInitialized=false;
      state.error = null;
    }
  },
  actions: {
    async authenticateUser({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_AUTH_ERROR', null);

      try {
        const response = await api.validateToken();

        commit('SET_USER_EMAIL', response.data.email);
        commit('SET_AUTHENTICATED', true);
        return true;
      } catch {
        commit('SET_AUTHENTICATED', false);
        commit('SET_AUTH_ERROR', 'Unable to validate your session.');
        return false;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async initializeBot({commit}){
      commit('SET_LOADING',true);
      commit('SET_ERROR',null);
      try{
        const response = await api.initBot();
        const {message,products}=response.data;
        commit('SET_INIT_MESSAGE',message);
        commit('SET_PRODUCTS',products);
        commit('SET_INITIALIZED',true);

        commit('ADD_MESSAGE',{
          sender:'assistant',
          text:message,
          type:'product-selection',
          products:products,
          timestamp:new Date()
        });
      }catch(error){
        commit('SET_ERROR', 'Failed to initialize bot');
        console.error('Error initializing bot:', error);
      } finally{
        commit('SET_LOADING', false);
      }
    },

    async selectProduct({commit,state},{productId,productName}){
      commit('SET_SELECTED_PRODUCT', { productId, productName });
      commit('SET_PRODUCT_SELECTED', true);

      commit('ADD_MESSAGE',{
        sender : 'user',
        text : productName,
        timestamp: new Date()
      });
      commit('SET_LOADING',true);
      commit('SET_ERROR',null);
      try{
        const response = await api.startConversation(productId,state.userEmail);
        const {conversationId , initialMessage, state:conversationState} = response.data;
        commit('SET_CONVERSATION_ID', conversationId);
        commit('SET_CONVERSATION_STATE', conversationState);

        commit('ADD_MESSAGE',{
          sender:'assistant',
          text: initialMessage,
          timestamp: new Date()
        });
        return conversationId;
      } catch(error) {
        commit('SET_ERROR', 'Failed to start conversation');
        console.error('Error starting conversation:', error);
      } finally {
        commit('SET_LOADING',false);
      }
    },

    async sendMessage({commit, state}, payload){
      commit('SET_ERROR',null);
      let text = '';
      let files = [];
      if (typeof payload === 'string') {
        text = payload;
      } else if (payload && typeof payload === 'object') {
        text = payload.text;
        files = payload.files || [];
      }

      if (files && files.length > 0) {
        commit('SET_LOADING', true);
        try {
          const formData = new FormData();
          files.forEach(f => {
            formData.append('files', f);
          });
          await api.uploadAttachments(state.conversationId, formData);
        } catch (error) {
          commit('SET_ERROR', 'Failed to upload attachments');
          commit('SET_LOADING', false);
          console.error('Error uploading attachments', error);
          return;
        }
      }

      // Build a single message payload that includes text and attachment metadata together.
      let userMessage = text && text.trim() ? text.trim() : '';
      if (files && files.length > 0) {
        const attachmentLines = files.map(f => `📎 Attached file: ${f.name}`);
        userMessage = `${userMessage}${userMessage ? '\n' : ''}${attachmentLines.join('\n')}`;
      }

      if (userMessage) {
        commit('ADD_MESSAGE', {
          sender: 'user',
          text: userMessage,
          timestamp: new Date()
        });

        commit('SET_LOADING', true);
        try {
          const response = await api.sendMessage(state.conversationId, userMessage);
          const { reply, state: conversationState } = response.data;
          commit('SET_CONVERSATION_STATE', conversationState);
          commit('ADD_MESSAGE', {
            sender: 'assistant',
            text: reply,
            timestamp: new Date()
          });
        } catch (error) {
          commit('SET_ERROR', 'Failed To Send Message');
          console.error('Error Sending Message', error);
        } finally {
          commit('SET_LOADING', false);
        }
      } else {
        commit('SET_LOADING', false);
      }
    },

    resetConversation({ commit }) {
      commit('RESET_CONVERSATION');
    },

    async handleAuthFailure({ commit }) {
      commit('SET_AUTHENTICATED', false);
      commit('SET_USER_EMAIL', '');
      commit('SET_AUTH_ERROR', 'Session expired. Please sign in again.');
      commit('RESET_CONVERSATION');

      if (router.currentRoute.path !== '/login') {
        await router.replace('/login');
      }
    }

  },
  modules: {
  }
})
